// v2 driftwood — one weathered forked log: a silver-bleached trunk lying low with
// a fork peeling away toward the tip, a snapped root plate at the butt, a broken
// branch stub jutting up mid-trunk and a dark knot in the bark. Variants flip the
// fork side, swing the log round and swap the bleach tone. Ported 1:1 from
// age-of-mcp's v2/driftwood.ts, using the library's shared `groundcover` (dirAt,
// tiltRot) helpers.
import * as THREE from "three";
import { defineModel, groundcover, type Part } from "../../lib";

const WOOD: [string, string, string][] = [
  ["#b6a78f", "#c4b8a2", "#8f8068"],   // silvery bleach
  ["#a99a80", "#bbac92", "#7d7058"],   // warmer, older wood
];

function driftwoodParts(v: number): Part[] {
  const [main, pale, dark] = WOOD[v % WOOD.length]!;
  const a0 = v * 1.9 + 0.35;                  // the log's compass heading
  const side = v % 2 === 0 ? 1 : -1;          // which way the fork peels
  const tilt = Math.PI / 2 - 0.06;            // almost flat, tip up a touch
  const d = groundcover.dirAt(a0, tilt);
  const c: [number, number, number] = [0, 0.056, 0];
  const at = (s: number): [number, number, number] =>
    [c[0] + d[0] * s, c[1] + d[1] * s, c[2] + d[2] * s];
  // the fork — peels away near the tip and rises a little
  const fb = at(0.08);
  const fa = a0 + side * 0.55;
  const ft = Math.PI / 2 - 0.18;
  const f = groundcover.dirAt(fa, ft);
  // the broken stub — juts up from mid-trunk
  const sb = at(-0.05);
  const sa = a0 - side * 1.2;
  const sd = groundcover.dirAt(sa, 0.5);
  const knot = at(0.16);
  const parts: Part[] = [
    // the bleached trunk, tapering toward the fork end
    { geometry: new THREE.CylinderGeometry(0.046, 0.06, 0.42, 6),
      color: main, position: c, rotation: groundcover.tiltRot(a0, tilt) },
    // snapped root plate at the butt
    { geometry: new THREE.CylinderGeometry(0.05, 0.058, 0.032, 7),
      color: pale, position: at(-0.215), rotation: groundcover.tiltRot(a0, tilt) },
    // the fork branch
    { geometry: new THREE.CylinderGeometry(0.024, 0.037, 0.26, 5),
      color: dark,
      position: [fb[0] + f[0] * 0.13, fb[1] + f[1] * 0.13, fb[2] + f[2] * 0.13],
      rotation: groundcover.tiltRot(fa, ft) },
    // broken branch stub
    { geometry: new THREE.CylinderGeometry(0.015, 0.024, 0.09, 5),
      color: pale,
      position: [sb[0] + sd[0] * 0.045, sb[1] + sd[1] * 0.045, sb[2] + sd[2] * 0.045],
      rotation: groundcover.tiltRot(sa, 0.5) },
    // a dark knot in the bark
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: dark,
      position: [knot[0], knot[1] + 0.038, knot[2]], scale: [1.4, 0.55, 1.0] },
  ];
  return parts;
}

export const driftwoodV2 = defineModel({
  id: "driftwood-v2",
  batchId: "fable",
  name: "Driftwood (v2)",
  tags: ["scenery","driftwood","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly driftwood.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.26,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => driftwoodParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => driftwoodParts(1) },
  ],
  animations: [],
});
